import { useState, useEffect, useRef } from 'react';

import * as help from './ManualAddressForm.helpers';
import {
  settlementTypes,
  streetTypes,
  houseTypes,
  buildingTypes,
  apartmentTypes,
} from './ManualAddressForm.constants';
import { manualAddressFormValidation } from './ManualAddressForm.validation';

export const useManualAddressFormPresenter = ({ initData = {}, onSave, onCancel }) => {
  const [localData, setLocalData] = useState({});
  const [settlementType, setSettlementType] = useState(null);
  const [streetType, setStreetType] = useState(null);
  const [houseType, setHouseType] = useState(null);
  const [buildingType, setBuildingType] = useState(null);
  const [apartmentType, setApartmentType] = useState(null);
  const [errors, setErrors] = useState({});
  const cityNameRef = useRef();
  const settlementNameRef = useRef();
  const hintRef = useRef();

  const presenter = {
    localData,
    settlementType,
    streetType,
    houseType,
    buildingType,
    apartmentType,
    errors,
    cityNameRef,
    settlementNameRef,
    hintRef,

    get daDataAdditionalData() {
      return {
        region: {
          from_bound: {
            value: 'region',
          },
          to_bound: {
            value: 'region',
          },
        },
        district: {
          from_bound: {
            value: 'area',
          },
          to_bound: {
            value: 'area',
          },
        },
        city: {
          from_bound: {
            value: 'city',
          },
          to_bound: {
            value: 'city',
          },
        },
      };
    },

    get settlementTypesList() {
      return help.getSettlementTypesList();
    },

    get streetTypesList() {
      return help.getStreetTypesList();
    },

    get houseTypesList() {
      return help.getHouseTypesListList();
    },

    get buildingTypesList() {
      return help.getBuildingTypesList();
    },

    get apartmentTypesList() {
      return help.getApartmentTypesList();
    },

    get isCityNameDisabled() {
      return Boolean(localData.settlementName);
    },

    get isSettlementTypeDisabled() {
      return Boolean(localData.cityName);
    },

    get isSettlementNameDisabled() {
      return Boolean(localData.cityName);
    },

    get region() {
      return localData.regionWithType || localData.regionName || '';
    },

    get district() {
      return localData.districtWithType || localData.districtName || '';
    },

    get city() {
      return localData.cityWithType || localData.cityName || '';
    },

    clearError(field) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    },

    localDataChangeHandler(data) {
      setLocalData((prev) => {
        return { ...prev, ...data };
      });
      const field = Object.keys(data)[0];
      presenter.clearError(field);
      if (field === 'settlementName') {
        presenter.clearError('cityName');
        presenter.clearError('cityFiasId');
        presenter.clearCity();
      }
      if (field === 'streetName') {
        setLocalData((prev) => ({ ...prev, streetKladrCode: '', streetKladrId: '' }));
      }
    },

    settlementTypeChangeHandler({ id }) {
      const listObj = help.findItemById(presenter.settlementTypesList, id);
      setSettlementType(listObj);
      presenter.clearError('settlementType');
    },

    streetTypeChangeHandler({ id }) {
      const listObj = help.findItemById(presenter.streetTypesList, id);
      setStreetType(listObj);
      presenter.clearError('streetType');
    },

    houseTypeChangeHandler({ id }) {
      const listObj = help.findItemById(presenter.houseTypesList, id);
      setHouseType(listObj);
      presenter.clearError('houseType');
    },

    buildingTypeChangeHandler({ id }) {
      const listObj = help.findItemById(presenter.buildingTypesList, id);
      setBuildingType(listObj);
      presenter.clearError('buildingType');
    },

    apartmentTypeChangeHandler({ id }) {
      const listObj = help.findItemById(presenter.apartmentTypesList, id);
      setApartmentType(listObj);
      presenter.clearError('apartmentType');
    },

    regionChangeHandler(res) {
      setErrors((prev) => ({ ...prev, regionName: '', regionFiasId: '' }));
      setLocalData((prev) => ({
        ...prev,
        regionName: res.data?.region || res?.value || '',
        regionType: res.data?.region_type || '',
        regionTypeFull: res.data?.region_type_full || '',
        region: res.data?.region_with_type || '',
        regionWithType: res.data?.region_with_type || '',
        regionFiasId: res.data?.region_fias_id || '',
        regionKladrCode: res.data?.region_kladr_id || '',
      }));
    },

    districtChangeHandler(res) {
      setErrors((prev) => ({ ...prev, districtName: '', districtKladrCode: '' }));
      setLocalData((prev) => ({
        ...prev,
        districtName: res.data?.area || res?.value || '',
        districtType: res.data?.area_type || '',
        districtTypeFull: res.data?.area_type_full || '',
        districtWithType: res.data?.area_with_type || '',
        districtFiasId: res.data?.area_fias_id || '',
        districtKladrCode: res.data?.area_kladr_id || '',
        areaFiasId: res.data?.area_fias_id || '',
        areaKladrCode: res.data?.area_kladr_id || '',
        areaType: res.data?.area_type || '',
        areaTypeFull: res.data?.area_type_full || '',
        areaWithType: res.data?.area_with_type || '',
      }));
    },

    cityChangeHandler(res) {
      setErrors((prev) => ({ ...prev, cityName: '', cityFiasId: '', settlementName: '' }));
      setLocalData((prev) => ({
        ...prev,
        city: res.data?.city || res?.value || '',
        cityName: res.data?.city || res?.value || '',
        cityType: res.data?.city_type || '',
        cityTypeFull: res.data?.city_type_full || '',
        cityWithType: res.data?.city_with_type || '',
        cityFiasId: res.data?.city_fias_id || '',
        cityKladrCode: res.data?.city_kladr_id || '',
        settlementName: '',
        settlementType: '',
        settlementTypeFull: '',
      }));
      setSettlementType(null);
    },

    clearCity() {
      setLocalData((prev) => ({
        ...prev,
        city: '',
        cityName: '',
        cityType: '',
        cityTypeFull: '',
        cityWithType: '',
        cityFiasId: '',
        cityKladrCode: '',
      }));
    },

    prepareDaDataRegionModifier(data) {
      return {
        ...data,
        from_bound: {
          value: 'region',
        },
        to_bound: {
          value: 'region',
        },
      };
    },

    prepareDaDataDistrictModifier(data) {
      return {
        ...data,
        from_bound: {
          value: 'area',
        },
        to_bound: {
          value: 'area',
        },
      };
    },

    prepareDaDataCityModifier(data) {
      return {
        ...data,
        from_bound: {
          value: 'city',
        },
        to_bound: {
          value: 'city',
        },
      };
    },

    validate() {
      try {
        manualAddressFormValidation.validateSync(
          {
            ...localData,
            apartmentType,
            buildingType,
            houseType,
            streetType,
            settlementType,
          },
          { abortEarly: false }
        );
      } catch (err) {
        const errors = help.getValidationErrors(err);
        setErrors((prev) => ({
          ...prev,
          ...errors,
        }));
        throw err;
      }
    },

    saveHandler() {
      setErrors({});
      try {
        presenter.validate();
      } catch (e) {
        return;
      }

      const data = help.prepareResultObject(
        localData,
        apartmentType,
        buildingType,
        houseType,
        streetType,
        settlementType
      );

      onSave(data);
    },

    cancelHandler() {
      onCancel();
    },
  };

  useEffect(() => {
    setLocalData(initData);

    if (initData.apartmentType) {
      const apartmentType = help.findEntityByType(initData.apartmentType, apartmentTypes);
      const listObj = help.findItemById(presenter.apartmentTypesList, apartmentType?.id);
      setApartmentType(listObj || null);
    }

    if (initData.buildingType) {
      const buildingType = help.findEntityByType(initData.buildingType, buildingTypes);
      const listObj = help.findItemById(presenter.buildingTypesList, buildingType?.id);
      setBuildingType(listObj || null);
    }

    if (initData.houseType) {
      const houseType = help.findEntityByType(initData.houseType, houseTypes);
      const listObj = help.findItemById(presenter.houseTypesList, houseType?.id);
      setHouseType(listObj || null);
    }

    if (initData.streetType) {
      const streetType = help.findEntityByType(initData.streetType, streetTypes);
      const listObj = help.findItemById(presenter.streetTypesList, streetType?.id);
      setStreetType(listObj || null);
    }

    if (initData.settlementType) {
      const settlementType = help.findEntityByType(initData.settlementType, settlementTypes);
      const listObj = help.findItemById(presenter.settlementTypesList, settlementType?.id);
      setSettlementType(listObj || null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return presenter;
};
